import {Grid, Theme, Typography, useMediaQuery, useTheme} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import React, {useEffect, useState} from 'react';
import clsx from 'clsx';
import moment, {Moment} from 'moment';
import RoomParty from './RoomParty';
import {formatDate} from '../../../utils/formatters';
import {
    BookingData,
    getGroupNightsData,
    getRoomsDataOptions,
    getTotalBookingCost,
    GuestData,
    RoomData
} from '../helperFunctions';
import {Group} from '../../../graphql/groups.datatypes';

const rainDrops = require('../../../../assets/img/rain-date.png');

const formattedCost = (cost: number) => {
    const format = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'});
    return format.format(cost).replace(/\.00$/, '')
}

const useStyles = (adminVersion?: boolean) => makeStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(0),
        paddingBottom: theme.spacing(3),

        [theme.breakpoints.down('sm')]: {
            margin: theme.spacing(3),
        },
    },
    padBottom: {
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3),
    },
    heading: {
        fontFamily: 'Recoleta-SemiBold',
        paddingTop: theme.spacing(3),
    },
    body: {
        fontFamily: 'Nunito',
        fontSize: 18,
    },
    nowrap: {
        whiteSpace: 'nowrap',
    },
    rainDate: {
        position: 'relative',
        backgroundColor: 'rgba(48,0,157,1)',
        padding: '24px !important',
        marginTop: 48,
        marginBottom: 48,
        color: 'rgba(255,255,255,1)',
    },
    rainDateDiv: {
        width: '83%',
        position: 'relative',
        paddingLeft: 15,
        paddingRight: 15,
        fontSize: 16,
    },
    rainDrops: {
    position: 'absolute',
    top: '-1.15rem',
    right: '-1.15rem',
    padding: '1.75rem',
    backgroundColor: 'rgba(255,255,255,.15)',
    borderRadius: '50%',

    },
    mandatoryDates: {
        borderRadius: 0,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: theme.palette.secondary.main,
        textAlign: 'center',
        minHeight: 40,
    },
    datesBlock: {
        fontFamily: 'Nunito',
        fontSize: 18,
        backgroundColor: 'rgba(173,2,163, 0.1)',
        marginTop: theme.spacing(1.5),
        marginBottom: theme.spacing(4),

        [theme.breakpoints.down('xs')]: {
            fontSize: 14,
        },

        '.MuiGrid-spacing-xs-1 > & .MuiGrid-item': {
            padding: theme.spacing(1.5),

            [theme.breakpoints.down('xs')]: {
                padding: theme.spacing(1),
            }
        },
    },
    datesBlockCheckIn: {
        borderColor: '#f2f2f2',
        borderStyle: 'solid',
        borderTopWidth: 2,
        borderBottomWidth: 2,
        borderLeftWidth: 0,
        borderRightWidth: 1,
    },
    datesBlockCheckOut: {
        borderColor: '#f2f2f2',
        borderStyle: 'solid',
        borderTopWidth: 2,
        borderBottomWidth: 2,
        borderLeftWidth: 1,
        borderRightWidth: 0,
    },
    datesBlockDisclosure: {
        borderColor: '#f2f2f2',
        borderStyle: 'solid',
        borderWidth: 0,
        borderTopWidth: 2,
    },
    textCenter: {
        width: '100%',
        textAlign: 'center',
    },
    daysContainer: {
        [theme.breakpoints.down('xs')]: {
            justifyContent: 'left',
        },
        [theme.breakpoints.up('sm')]: {
            justifyContent: 'center',
            flexWrap: 'nowrap',
        },
    },
    dayBlock: {
        borderRadius: 0,
        borderStyle: 'solid',
        borderWidth: 1,
        textAlign: 'center',
        borderColor: theme.palette.grey[300],
        fontFamily: 'Nunito',
        fontSize: 16,

        [theme.breakpoints.down('xs')]: {
            paddingTop: theme.spacing(1),
            width: '14.2%',
            listStyleType: 'none',
            display: 'inline-block',
            verticalAlign: 'middle',
            fontSize: 12,
            position: 'relative',
            height: 0,
            paddingBottom: '14.2%',
        },
        [theme.breakpoints.only('sm')]: {
            paddingTop: theme.spacing(1),
            minWidth: 60,
            maxWidth: 60,
            height: 70,
            fontSize: 14,
        },
        [theme.breakpoints.only('md')]: {
            paddingTop: theme.spacing(2),
            minWidth: 80,
            maxWidth: 80,
            height: 95,
        },
        [theme.breakpoints.up('lg')]: {
            paddingTop: theme.spacing(2),
            minWidth: 85,
            maxWidth: 85,
            height: 100,
        },
    },
    selectedDay: {
        backgroundColor: '#d7a3ff',
        borderColor: theme.palette.secondary.main,
        cursor: 'pointer',
    },
    availableDay: {
        backgroundColor: theme.palette.white,
        borderColor: theme.palette.grey[300],
        cursor: 'pointer',
    },
    checkInDay: {
        backgroundColor: '#d7a3ff',
        backgroundImage: '-webkit-linear-gradient(40deg, white 50%, #d7a3ff 50%)',
        borderLeftColor: theme.palette.grey[300],
        borderBottomColor: theme.palette.grey[300],
    },
    checkOutDay: {
        backgroundColor: '#d7a3ff',
        backgroundImage: '-webkit-linear-gradient(40deg, #d7a3ff 50%, white 50%)',
        borderColor: theme.palette.secondary.main,
        borderRightColor: theme.palette.grey[300],
        borderTopColor: theme.palette.grey[300],
    },
    unavailableDay: {
        backgroundColor: '#fff',
        color: '#ccc',
    },
    blackText: {
        color: theme.palette.black,
    },
    whiteText: {
        color: theme.palette.white,
    },
    grandTotal: {
        position: 'sticky',
        bottom: adminVersion ? '0px' : '74px',
        fontFamily: 'Nunito',
        fontSize: 18,
        fontWeight: 'bold',
        marginTop: theme.spacing(4.5),
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.white,

        '&:before, &:after': {
            content: '""',
            position: 'absolute',
            top: 0,
            width: '100vw',
            height: '100%',
            backgroundColor: 'inherit',
        },

        '&:before': {
            left: '-100vw'
        },

        '&:after': {
            left: '100%'
        }
    },
    textRight: {
        textAlign: 'right'
    },
    weekdays: {
        '& li': {
            backgroundColor: theme.palette.primary.main,
            fontFamily: 'Nunito',
            display: 'inline-block',
            width: '14.2%',
            color: theme.palette.white,
            textAlign: 'center',
            height: 45,
            paddingTop: 10,
        }
    },

    days: {},

    subHeader: {
        fontFamily: 'Nunito',
        fontWeight: 'bold',
        fontSize: 20
    },

    constrainedContent: {
        [theme.breakpoints.up('sm')]: {
            maxWidth: '78%',
            marginLeft: 'auto',
            marginRight: 'auto',
        }
    },
}));

interface Props {
    setActiveStep: (num: number) => void;
    group: Group;
    bookingData: BookingData;
    setBookingData: (data: BookingData) => void;
    allGuestData: GuestData[];
    setGroup: (group: Group) => void;
    adminVersion?: boolean;
}

const Step3OptionalExtras = (props: Props) => {
    const {group, bookingData, setBookingData, allGuestData, setGroup, adminVersion} = props;
    const classes = useStyles(adminVersion)();
    const [previewNights, setPreviewNights] = useState<{ start: Moment, end: Moment } | null>(null);
    const theme = useTheme();

    const isMobile = useMediaQuery(theme.breakpoints.down('xs'), {
        defaultMatches: true
    });

    const [groupNights] = useState(getGroupNightsData(group));
    const totalNightsAllowed = groupNights.mandatoryNightsCount + groupNights.maxAdditionalNights;

    const bookingLength = bookingData.nights.end.diff(bookingData.nights.start, 'days');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleMouseEnter = (date: Moment) => {

        if (date.isSameOrBefore(groupNights.mandatoryStart)) {
            if (bookingData.nights.end.diff(date, 'days') <= totalNightsAllowed) {
                setPreviewNights({start: date, end: bookingData.nights.end});
                setErrorMessage(null);
            } else {
                setErrorMessage('Selecting this check in date will make your stay too long.');
            }
        } else if (date.isSameOrAfter(groupNights.mandatoryEnd)) {
            if (date.diff(bookingData.nights.start, 'days') <= totalNightsAllowed) {
                setPreviewNights({start: bookingData.nights.start, end: date});
                setErrorMessage(null);
            } else {
                setErrorMessage('Selecting this check out date will make your stay too long.');
            }
        }
    };

    const handleMouseLeave = () => {
        setPreviewNights(null);
        setErrorMessage(null);
    };

    const setCheckInDate = (date: Moment) => {
        if (bookingData.nights.end.diff(date, 'days') <= totalNightsAllowed) {
            setBookingData({...bookingData, nights: {...bookingData.nights, start: date}});
            setErrorMessage(null);
        } else {
            setErrorMessage('Selecting this check in date will make your stay too long.');
        }
    };

    const setCheckOutDate = (date: Moment) => {
        if (date.diff(bookingData.nights.start, 'days') <= totalNightsAllowed) {
            setBookingData({...bookingData, nights: {...bookingData.nights, end: date}});
            setErrorMessage(null);
        } else {
            setErrorMessage('Selecting this check out date will make your stay too long.');
        }
    };

    useEffect(() => {
        const roomsData = getRoomsDataOptions(group, bookingData, bookingData.roomsData);
        setBookingData({...bookingData, roomsData});
    }, [bookingData.nights]);

    const setRoomData = (targetData: RoomData) => {
        const filteredRooms: RoomData[] = bookingData.roomsData.map(roomData => roomData.id !== targetData.id ? roomData : targetData);
        const roomsData = getRoomsDataOptions(group, bookingData, filteredRooms);
        setBookingData({...bookingData, roomsData});
    };

    const checked: string[] = [];
    const roomCounts = group.hotel!.roomResources!
        .sort((a, b) => a.guest_capacity! - b.guest_capacity!)
        .map(roomBank => ({
                roomCount: bookingData.roomsData
                    .filter(roomData => {
                        if (roomData.guestsData.length <= roomBank.guest_capacity! && checked.indexOf(roomData.id) === -1) {
                            checked.push(roomData.id);
                            return true;
                        }
                        return false;
                    }).length,
                guestCapacity: roomBank.guest_capacity,
                roomBanks: roomBank.resource_banks!
                    .filter(item => {
                        const bankDate = moment(item.date);
                        return bankDate.isSameOrAfter(groupNights.availableNightsStart) && bankDate.isSameOrBefore(groupNights.availableNightsEnd);
                    })
            }
        ));

    // @ts-ignore
    const arrDays = [];
    const displayNights = previewNights === null ? bookingData.nights : previewNights;
    for (let i = 0; i <= groupNights.availableNightsCount; i++) {
        const date = groupNights.availableNightsStart.clone().add(i, 'days');
        const newCapacity = roomCounts.map(item => {
            const roomBank = item.roomBanks.filter(bank => date.isSame(bank.date, 'day')).pop();
            if (roomBank) return roomBank.remaining! >= item.roomCount;
            return false;
        }).reduce((accum: boolean, check, j) => (j === 0) ? check : accum || check, false);

        // @ts-ignore
        const newCanCheckOut: boolean = date.isSameOrAfter(groupNights.mandatoryEnd) && i > 0 && arrDays[i-1].capacity;
        arrDays.push({
            date,
            booking: !(date.isBefore(displayNights.start) || date.isAfter(displayNights.end)),
            clickHandler: date.isSameOrBefore(groupNights.mandatoryStart) ? setCheckInDate : date.isSameOrAfter(groupNights.mandatoryEnd) ? setCheckOutDate : undefined,
            checkIn: date.isSame(displayNights.start, 'day'),
            checkOut: date.isSame(displayNights.end, 'day'),
            padding: false,
            capacity: newCapacity,
            canCheckOut: newCanCheckOut,
        });
    }

    //Make sure available dates with capacity are contiguous.
    let capacity = true;
    let canCheckOut = true;
    for (let i = arrDays.findIndex(item => groupNights.mandatoryEnd.isSame(item.date, 'day')); i < arrDays.length; i++) {
        const item = arrDays[i];
        if (!item.capacity) capacity = false;
        if (!item.canCheckOut) canCheckOut = false;
        item.capacity = capacity;
        item.canCheckOut = canCheckOut;
    }
    capacity = true;
    for (let i = arrDays.findIndex(item => groupNights.mandatoryStart.isSame(item.date, 'day')); i >= 0; i--) {
        const item = arrDays[i];
        if (!item.capacity) capacity = false;
        item.capacity = capacity;
    }

    if (isMobile) {
        //Add days to start to allow starting on sunday.
        while (arrDays[0].date.weekday() > 0) {
            arrDays.unshift({
                date: arrDays[0].date.clone().subtract(1, 'days'),
                booking: false,
                clickHandler: undefined,
                checkIn: false,
                checkOut: false,
                padding: true,
                capacity: false,
                canCheckOut: false,
            })
        }
        //add days to end to stop on a saturday.
        while (arrDays[arrDays.length - 1].date.weekday() !== 6) {
            arrDays.push({
                date: arrDays[arrDays.length - 1].date.clone().add(1, 'days'),
                booking: false,
                clickHandler: undefined,
                checkIn: false,
                checkOut: false,
                padding: true,
                capacity: false,
                canCheckOut: false,
            })
        }
    }

    const totalBookingCost = getTotalBookingCost(group, bookingData, allGuestData);

    return (
        <div className={classes.root}>
            <Grid container spacing={1}>
                <Grid item xs={12} className={classes.constrainedContent}>
                    <Typography
                        variant="h1"
                        align="center"
                        className={clsx(classes.heading, classes.padBottom)}
                    >
                        Additional Nights
                    </Typography>
                    <Typography variant="body1" className={clsx([classes.body, classes.padBottom])}>
                       Your standard package currently includes a {groupNights.mandatoryNightsCount}-night stay
                       from <b>{groupNights.mandatoryStart.format('MMMM D')} – {groupNights.mandatoryEnd.format('MMMM D, YYYY')}</b>.
                       If you would like to extend your stay, please select your desired check in and check out dates,
                       up to a <b>maximum {totalNightsAllowed}-night stay</b>.
                   </Typography>
                </Grid>

                {!isMobile && (
                    <Grid item xs={12}>
                        <Grid container spacing={0} className={clsx([classes.daysContainer, classes.padBottom])}>
                            {arrDays.map((item, i) => (
                                <Grid item key={i} md={1} xs={2}
                                      onClick={() => (item.capacity || item.canCheckOut) && item.clickHandler && item.clickHandler(item.date)}
                                      onMouseEnter={() => (item.capacity || item.canCheckOut) && handleMouseEnter(item.date)}
                                      onMouseLeave={() => (item.capacity || item.canCheckOut) && handleMouseLeave()}
                                      className={clsx({
                                          [classes.dayBlock]: true,
                                          [classes.selectedDay]: item.booking,
                                          [classes.availableDay]: !item.booking && !item.padding,
                                          [classes.checkInDay]: item.checkIn,
                                          [classes.checkOutDay]: item.checkOut,
                                          [classes.unavailableDay]: item.padding || !(item.capacity || item.canCheckOut),
                                      })}>
                                    <span>
                                        {item.date.format('ddd')}<br/>
                                        {item.date.format('MMMM').length <= 4 ? item.date.format('MMMM') : item.date.format('MMM')}<br/>
                                        <b>{item.date.format('D')}</b>
                                    </span>

                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                )}

                {isMobile && (
                    <Grid item xs={12}>

                        <ul className={classes.weekdays}>
                            <li>Su</li>
                            <li>Mo</li>
                            <li>Tu</li>
                            <li>We</li>
                            <li>Th</li>
                            <li>Fr</li>
                            <li>Sa</li>
                        </ul>

                        <ul className={classes.days}>
                            {arrDays.map((item, i) => (
                                <li key={i}
                                    onClick={() => (item.capacity || item.canCheckOut) && item.clickHandler && item.clickHandler(item.date)}
                                    onMouseEnter={() => (item.capacity || item.canCheckOut) && handleMouseEnter(item.date)}
                                    onMouseLeave={() => (item.capacity || item.canCheckOut) && handleMouseLeave()}
                                    className={clsx({
                                        [classes.dayBlock]: true,
                                        [classes.selectedDay]: item.booking,
                                        [classes.availableDay]: !item.booking && !item.padding,
                                        [classes.checkInDay]: item.checkIn,
                                        [classes.checkOutDay]: item.checkOut,
                                        [classes.unavailableDay]: item.padding || !(item.capacity || item.canCheckOut),
                                    })}
                                >
                                    {item.date.format('MMMM').length <= 4 ? item.date.format('MMMM') : item.date.format('MMM')}<br/>
                                    {item.date.format('Do')}
                                </li>
                            ))}
                        </ul>

                        <br/><br/>
                    </Grid>
                )}

                {errorMessage && (
                    <Grid item xs={12}>
                        <Typography variant="body1" color="error" className={classes.textCenter}>
                            {errorMessage}
                        </Typography>
                    </Grid>
                )}

              {group.tour!.rain_date_note! && group.tour!.rain_date_note!.replace(/(\r\n|\n|\r|<\/br>|<br\/>|<br \/>|<br>|<p>|<\/p>)/gm,'').trim().length > 0 && (

                <Grid item xs={12} className={clsx([classes.constrainedContent, classes.rainDate])}>
                    <div className={classes.rainDrops}><img
                        alt="Rain Drops"
                        src={rainDrops}
                    /></div>
                    <div className={clsx([classes.body, classes.padBottom, classes.rainDateDiv])}
                         dangerouslySetInnerHTML={{__html: group.tour!.rain_date_note!}}/>
                </Grid>
                  )}

                <Grid item xs={12}>
                    <Grid item>
                        <Typography variant="h3" className={clsx(classes.subHeader, classes.constrainedContent)}>My
                            Stay</Typography>
                    </Grid>
                </Grid>

                <Grid item xs={12} className={clsx(classes.datesBlock, classes.constrainedContent)}>
                    <Grid container spacing={1}>
                        <Grid item xs={12} className={classes.nowrap}>
                            <strong>Hotel:</strong> {group.hotel!.display_name}
                        </Grid>
                        <Grid item xs={6} className={clsx([classes.nowrap, classes.datesBlockCheckIn])}>
                            <strong>Check In:</strong> {formatDate(bookingData.nights.start)}
                        </Grid>
                        <Grid item xs={6} className={clsx([classes.nowrap, classes.datesBlockCheckOut])}>
                            <strong>Check Out:</strong> {formatDate(bookingData.nights.end)}
                        </Grid>
                        <Grid item xs={12} className={classes.nowrap}>
                            <strong>Duration:</strong> {bookingLength}-Nights
                        </Grid>
                        <Grid item xs={12} className={classes.datesBlockDisclosure}>
                            <strong>Arrival/Departure Information:</strong><br/>
                            {group.description_checkin}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <Grid container spacing={1} className={classes.constrainedContent}>
                <Grid item xs={12}>
                    <Typography variant="h1" className={classes.heading}>
                        Add-Ons
                    </Typography>
                    <Typography variant="body1" className={clsx([classes.body, classes.padBottom])}>
                        When you select certain options (including parkhopper tickets) other options may disappear. If the option you would like to change to is currently hidden, try unselecting current selections, and seeing if it appears.
                    </Typography>
                </Grid>

                {bookingData.roomsData.map((roomData, i) => (
                    <RoomParty
                        key={i}
                        bookingData={bookingData}
                        group={group}
                        roomData={roomData}
                        setRoomData={setRoomData}
                        setGroup={setGroup}
                    />
                ))}
            </Grid>
            <Grid container className={clsx([classes.grandTotal])}>
                <Grid item xs={6}>
                    Total
                </Grid>
                <Grid item xs={6} className={classes.textRight}>
                    {formattedCost(totalBookingCost)}
                </Grid>
            </Grid>
        </div>
    );
};

export default Step3OptionalExtras;
